import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { FaEllipsisV } from "react-icons/fa";
import DeleteModal from "../modal/DeleteModal";
import ViewUserModal from "../modal/ViewUserModal";
import { request } from "../../../utils/api";
import { toast } from "react-toastify";
import Loading from "../utilComponent/Loading";
import Pagination from "../utilComponent/Pagination";
import { formatYYYYMMDD } from "../../../utils";

const ServiceList = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const getServices = async (page = 1, search = searchText) => {
    try {
      setLoading(true);
      const response = await request({
        url: `services?page=${page}&per_page=${pagination.per_page}&search=${search}`,
        method: "GET",
      });

      if (response?.status && response?.data) {
        setServices(
          response.data.items.map((item) => ({
            id: item.id,
            service_id: item.id,
            title: item.title,
            description: item.description,
            timeline: item.timeline,
            price: item.price,
            qty: item.qty,
            created_at: formatYYYYMMDD(item.created_at),
          }))
        );
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getServices(1);
  }, []);

  const deleteService = async () => {
    try {
      setLoading(true);
      await request({
        url: `services/${selectedService.id}`,
        method: "DELETE",
      });

      toast.success("Service deleted successfully");
      setShowDeleteModal(false);
      setSelectedService(null);
      getServices(pagination.current_page);
    } catch {
      toast.error("Cannot delete service");
    } finally {
      setLoading(false);
    }
  };

  const serviceFields = [
    { label: "Service ID", key: "service_id" },
    { label: "Title", key: "title" },
    { label: "Price", key: "price" },
    { label: "Quantity", key: "qty" },
    { label: "Timeline", key: "timeline" },
    { label: "Description", key: "description", isHtml: true },
    { label: "Created Date", key: "created_at" },
  ];

  return (
    <div>
      <ViewUserModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        data={selectedService}
        fields={serviceFields}
        title="Service Details"
        onEdit={(item) => navigate(`/service-update/${item.id}`)}
      />

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={deleteService}
      />

      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Service</li>
            <li className="breadcrumb-item active">Service List</li>
          </ol>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
        <div className="container-fluid">
          <div className="card shadow-sm">
            <div className="card-header">
              <h6 className="card-title m-0">Service List</h6>
            </div>

            <div className="card-body">
              {/* Search */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    className="form-control"
                    placeholder="Search services..."
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      getServices(1, e.target.value);
                    }}
                  />
                </div>
              </div>

              {loading ? (
                <Loading />
              ) : (
                <div className="row">
                  {services.length ? (
                    services.map((item) => (
                      <div key={item.id} className="col-xl-4 col-lg-6 mb-4">
                        <div className="card h-100 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between mb-2">
                              <h6 className="fw-bold">{item.title}</h6>
                              <span className="text-muted">#{item.service_id}</span>
                            </div>

                            <div
                              className="text-muted mb-2"
                              style={{
                                maxHeight: "60px",
                                overflow: "hidden",
                              }}
                              dangerouslySetInnerHTML={{
                                __html: item.description,
                              }}
                            />

                            <p className="mb-1">
                              💰 <strong>Price:</strong> {item.price}
                            </p>
                            <p className="mb-1">
                              ⏳ <strong>Timeline:</strong> {item.timeline}
                            </p>
                            <p className="mb-1">
                              📦 <strong>Qty:</strong> {item.qty}
                            </p>
                            <p className="mb-3">
                              📅 <strong>Created:</strong> {item.created_at}
                            </p>

                            <div className="d-flex justify-content-end">
                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  variant="light"
                                  className="border-0 p-0 shadow-none"
                                >
                                  <FaEllipsisV />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() => {
                                      setSelectedService(item);
                                      setShowViewModal(true);
                                    }}
                                  >
                                    View
                                  </Dropdown.Item>

                                  <Dropdown.Item
                                    onClick={() => navigate(`/service-update/${item.id}`)}
                                  >
                                    Edit
                                  </Dropdown.Item>

                                  <Dropdown.Divider />

                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => {
                                      setSelectedService(item);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-5">No services found</div>
                  )}
                </div>
              )}

              {pagination.last_page > 1 && (
                <Pagination
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={(page) => getServices(page)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceList;
